import React, {ReactElement, useState} from 'react';
import {NavLink, useLocation} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {i18n, MessageDescriptor} from '@lingui/core';
import {t} from '@lingui/core/macro';
import cn from 'classnames';

import {
    Permission,
    IUserOutput,
    isGrantedAccess,
    MenuGroupKey,
    userPermissionsSelector,
    userSelector,
} from 'entities/user';
import {isTranslate} from 'shared/lib/lang';


import StatusToggle from 'shared/ui/StatusToggle/StatusToggle';
import OpenURL from 'shared/ui/icons/General/OpenURL';
import ArrowDown from 'shared/ui/icons/General/ArrowDown';

import {getTitleText} from 'widgets/layout/lib/getTitleText';

import {useNavigation} from '../../model/navigation';

import styles from './MainMenu.module.scss';

interface IMenuItemProps {
    path: string,
    title: string | MessageDescriptor,
    icon: ReactElement;
    auth?: Permission[],
    target?: string,
    // eslint-disable-next-line no-unused-vars
    condition?: (user: IUserOutput) => boolean,
    label?: React.ReactNode,
    menuGroup?: MenuGroupKey,
    menuLabel?: string | MessageDescriptor,
}

interface IToggledGroups {
    pathname: string,
    groups: Partial<Record<MenuGroupKey, boolean>>,
}

type MenuNode =
    | { kind: 'item', item: IMenuItemProps }
    | { kind: 'group', groupKey: MenuGroupKey, items: IMenuItemProps[] };

const isAdminItem = (item: IMenuItemProps): boolean => (
    Boolean(item.auth?.includes(Permission.ADMIN))
);

const isPathActive = (pathname: string, path: string): boolean => (
    pathname === path || pathname.startsWith(`${path}/`)
);

const MainMenu: React.FC = () => {
    const {menuItems, menuGroups} = useNavigation();
    const userPermissions = useSelector(userPermissionsSelector);
    const location = useLocation();
    const menu = menuItems
        .filter(item => item.title && item.icon)
        .filter(item => !item.hidden) as IMenuItemProps[];

    const isAdmin = userPermissions.includes(Permission.ADMIN);

    const [isAdminMode, setIsAdminMode] = useState(() => menu.some(item => (
        isAdminItem(item) && isPathActive(location.pathname, item.path)
    )));

    const [toggledGroups, setToggledGroups] = useState<IToggledGroups>({
        pathname: location.pathname,
        groups: {},
    });
    const groupToggles = toggledGroups.pathname === location.pathname ? toggledGroups.groups : {};

    const user = useSelector(userSelector);
    const isConditionFulfilled = (item: IMenuItemProps) => (
        !item.condition
        || (null !== user && item.condition(user))
    );

    const visibleMenu = menu.filter(item => (
        isAdminMode ? isAdminItem(item) : !isAdminItem(item)
    ));

    const isTargetBlank = (title: string | MessageDescriptor): boolean => {
        const titleText = getTitleText(title);
        return titleText === 'Новости'
            || titleText === 'Акции'
            || titleText === 'Тех. поддержка';
    };

    const isItemVisible = (item: IMenuItemProps) => (
        isGrantedAccess(userPermissions, item.auth) && isConditionFulfilled(item)
    );

    const iconColorClass = () => styles[isTranslate() ? 'enColorIcon' : 'ruColorIcon'];
    const titleColorClass = () => styles[isTranslate() ? 'enColorTitle' : 'ruColorTitle'];

    const renderMenuItem = (item: IMenuItemProps, isChild = false) => {
        const displayTitle = isChild ? item.menuLabel ?? item.title : item.title;

        return (
            <li
                className={cn(styles.item, {[styles.childItem]: isChild})}
                key={item.path}>
                <NavLink
                    to={item.path}
                    end={item.path.length === 1}
                    target={item.target}
                    className={({isActive}) => {
                        if (item.path?.includes('#')) {
                            return cn();
                        }
                        return cn(
                            { [styles.active_link]: isActive },
                        );
                    }}
                >
                    {
                        !isChild && (
                            <span className={cn(styles.icon, iconColorClass())}>
                                {getTitleText(item.title) === 'Тарифы' && user?.highlight_tariff && (
                                    <span className={styles.target}/>
                                )}
                                {React.cloneElement(item.icon, {
                                    isActive: isTranslate() && location.pathname === item.path,
                                })}
                            </span>
                        )
                    }
                    <span className={cn(
                        styles.titleBox,
                        { [styles.hasLabel]: Boolean(item.label) },
                    )}>
                        <span className={titleColorClass()}>
                            {typeof displayTitle === 'string'
                                ? displayTitle
                                : i18n._(displayTitle)}
                        </span>
                        {
                            item.label && <>{item.label}</>
                        }
                    </span>
                    {
                        isTargetBlank(item.title) && <span className={styles.iconLink}><OpenURL/></span>
                    }

                </NavLink>
            </li>
        );
    };

    const renderGroup = (groupKey: MenuGroupKey, items: IMenuItemProps[]) => {
        const visibleChildren = items.filter(isItemVisible);
        if (visibleChildren.length === 0) {
            return null;
        }

        const group = menuGroups[groupKey];
        const isGroupActive = visibleChildren.some(child => isPathActive(location.pathname, child.path));

        return (
            <li className={cn(styles.item, styles.group)} key={groupKey}>
                <details
                    open={groupToggles[groupKey] ?? isGroupActive}
                    onToggle={event => setToggledGroups({
                        pathname: location.pathname,
                        groups: {...groupToggles, [groupKey]: event.currentTarget.open},
                    })}
                >
                    <summary className={cn(
                        styles.groupSummary,
                        { [styles.active_link]: isGroupActive },
                    )}>
                        <span className={cn(styles.icon, iconColorClass())}>
                            {group.icon}
                        </span>
                        <span className={cn(styles.titleBox, titleColorClass())}>
                            {i18n._(group.title)}
                        </span>
                        <span className={styles.chevron}><ArrowDown/></span>
                    </summary>
                    <ul className={styles.submenu}>
                        {visibleChildren.map(child => renderMenuItem(child, true))}
                    </ul>
                </details>
            </li>
        );
    };

    const menuNodes = visibleMenu.reduce<MenuNode[]>((nodes, item) => {
        if (!item.menuGroup) {
            nodes.push({ kind: 'item', item });
            return nodes;
        }

        const existingGroup = nodes.find(
            (node): node is Extract<MenuNode, {kind: 'group'}> => (
                node.kind === 'group' && node.groupKey === item.menuGroup
            ),
        );
        if (existingGroup) {
            existingGroup.items.push(item);
        } else {
            nodes.push({ kind: 'group', groupKey: item.menuGroup, items: [item] });
        }
        return nodes;
    }, []);

    return (
        <>
            <ul className={cn(
                styles.list,
                isTranslate() ? styles.enColorScroll : '',
            )}>
                {
                    menuNodes.map(node => (
                        node.kind === 'group'
                            ? renderGroup(node.groupKey, node.items)
                            : (isItemVisible(node.item) ? renderMenuItem(node.item) : null)
                    ))
                }
            </ul>
            {
                isAdmin && (
                    <div className={styles.switcher}>
                        <StatusToggle
                            condition={isAdminMode}
                            onClick={() => setIsAdminMode(prev => !prev)}
                            textActive={t`Администрирование`}
                            notActiveText={t`Администрирование`}
                        />
                    </div>
                )
            }
        </>
    );
};

export default MainMenu;
